import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShieldCheck, Menu, X, LogOut } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/careers", label: "Careers" },
  { to: "/driver-of-the-week", label: "Driver of the Week" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-brand-link" className="flex items-center gap-3">
          <img src="/logo.png" alt="Integrity Delivered" data-testid="nav-logo" className="h-11 sm:h-12 w-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" />
          <div className="hidden sm:flex items-center gap-1 text-[10px] text-slate-400 uppercase tracking-widest border-l border-slate-700 pl-3">
            <ShieldCheck className="w-3 h-3" /> Independent Contractor
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "text-amber-400 bg-slate-900" : "text-slate-300 hover:text-white hover:bg-slate-900"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {user ? (
            <>
              {user.role === "recruiting_hr" && <Button data-testid="nav-recruiting-dashboard-btn" variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-slate-900" onClick={() => navigate("/admin")}>Recruiting</Button>}
              {user.role === "admin" && <Button data-testid="nav-admin-dashboard-btn" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-900" onClick={() => navigate("/admin")}>Dashboard</Button>}
              {user.role !== "recruiting_hr" && <Button data-testid="nav-ops-dashboard-btn" variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-slate-900" onClick={() => navigate("/ops")}>Operations</Button>}
              <Button data-testid="nav-logout-btn" variant="ghost" className="text-slate-300 hover:text-white" onClick={logout}>
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </>
          ) : (
            <Link to="/admin/login" data-testid="nav-admin-login-link" className="text-xs text-slate-400 hover:text-amber-400 uppercase tracking-widest">Admin</Link>
          )}
          <Button data-testid="nav-apply-now-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" onClick={() => navigate("/apply")}>
            Apply Now
          </Button>
        </div>

        <button className="lg:hidden text-white" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-800 bg-slate-950">
          <div className="px-4 py-3 space-y-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-mobile-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-md text-sm font-medium ${isActive ? "text-amber-400 bg-slate-900" : "text-slate-300 hover:bg-slate-900"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="pt-2 flex gap-2">
              <Link to="/apply" onClick={() => setOpen(false)} className="flex-1">
                <Button data-testid="nav-mobile-apply-btn" className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold">Apply Now</Button>
              </Link>
              {user ? (
                <Button data-testid="nav-mobile-logout-btn" variant="outline" className="border-slate-700" onClick={() => { logout(); setOpen(false); }}>Logout</Button>
              ) : (
                <Link to="/admin/login" onClick={() => setOpen(false)}>
                  <Button data-testid="nav-mobile-admin-btn" variant="outline" className="border-slate-700 text-slate-200">Admin</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
